import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconFilter } from '@tabler/icons-react';
import type { InterviewSession, ResponseStatus } from '../../../types';
import { getCampaignAnswerRows, type CampaignAnswerRow } from '../../../services/interviewService';
import { captureEvent, captureException } from '../../../lib/telemetry/posthog/frontend';
import { responseStatusConfig } from '../../../lib/admin-utils';
import { validateExportFilters, type CampaignExportFilters } from '../../../lib/campaign-export-filters';
import { buildOverview } from '../../../lib/campaign-analytics/overview';
import { drillDown, type DrillTarget } from '../../../lib/campaign-analytics/drill';
import { buildSuggestions } from '../../../lib/campaign-analytics/suggestions';
import type { QuestionSummary } from '../../../lib/campaign-analytics/summarize';
import { CampaignAnswerFilters } from '../CampaignAnswerFilters';
import { FilterChipRow } from '../../ui/FilterChipRow';
import { HeadlineStrip } from './HeadlineStrip';
import { HighlightsCarousel } from './HighlightsCarousel';
import { AskHollieCard } from './AskHollieCard';
import { SectionRows } from './SectionRows';
import { DropOffRow } from './DropOffCard';
import { TipLayer } from './TipLayer';

/** The respondents behind a clicked mark, for the response list. */
export interface ResponseSelection {
  ids: string[];
  label: string;
}

/** Props for {@link CampaignOverview}. */
export interface CampaignOverviewProps {
  /** Omit while the campaign metadata is loading. */
  session?: InterviewSession;
  /** Changes when the campaign's responses change, to reload answers. */
  refreshKey?: string | number;
  /** Opens the response list filtered to a selection. */
  onOpenResponses?: (selection: ResponseSelection) => void;
  /** Sends a question to Ask hollie. Omit when Ask hollie is not available. */
  onAsk?: (question: string) => void;
  /** The button that opens the Ask hollie chat, shown in the Ask hollie card's header. */
  askBox?: React.ReactNode;
  /** The written summary, shown left of the Ask hollie suggestions. */
  summary?: React.ReactNode;
  /** Completed recipe cards, integrated between the overview highlights and written sections. */
  recipeResults?: React.ReactNode;
  /** The written report sections (insights, quotes, themes, next steps), placed after the highlights. */
  report?: React.ReactNode;
  /** When the written report was last updated (with its refresh control), at the end of the filter row. */
  updated?: React.ReactNode;
}

/** The statuses worth filtering an overview by; the rest (pending, no credits) stay under All. */
const STATUS_OPTIONS: ResponseStatus[] = ['completed', 'in_progress'];

/** Default population: completed responses only. */
const DEFAULT_FILTERS: CampaignExportFilters = { status: 'completed' };

/** Whether filters differ from the default population (telemetry carries presence only). */
function hasFilters(filters: CampaignExportFilters): boolean {
  return filters.status !== DEFAULT_FILTERS.status || Boolean(filters.startDate || filters.endDate || filters.answers?.length);
}

/** Load answers-only rows, reporting a terminal failure once. */
function useAnswerRows(sessionId: string | undefined, refreshKey: string | number | undefined) {
  const [result, setResult] = useState<{ sessionId?: string; rows: CampaignAnswerRow[] | null; failed: boolean; loading: boolean }>({ sessionId, rows: null, failed: false, loading: true });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!sessionId) return undefined;
    const controller = new AbortController();
    setResult(previous => ({ sessionId, rows: previous.sessionId === sessionId ? previous.rows : null, failed: false, loading: true }));
    getCampaignAnswerRows(sessionId, controller.signal).then(
      rows => { if (!controller.signal.aborted) setResult({ sessionId, rows, failed: false, loading: false }); },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        captureException(error instanceof Error ? error : new Error('Campaign answers failed to load'), 'CampaignOverview.load', { interviewId: sessionId });
        setResult(previous => ({ ...previous, failed: true, loading: false }));
      },
    );
    return () => controller.abort();
  }, [sessionId, refreshKey, attempt]);
  const current = result.sessionId === sessionId ? result : { rows: null, failed: false, loading: true };
  return { ...current, retry: () => setAttempt(value => value + 1) };
}

/**
 * The deterministic campaign Overview, kept calm: three friendly numbers, a
 * carousel of the clearest results, Ask hollie as the way into detail, and
 * every section collapsed until opened. Every number is computed by code from
 * stored answers with the CSV export's filters.
 */
export const CampaignOverview: React.FC<CampaignOverviewProps> = ({ session, refreshKey, onOpenResponses, onAsk, askBox, summary, recipeResults, report, updated }) => {
  const sessionId = session?.id;
  const fields = useMemo(() => session?.form_schema ?? [], [session?.form_schema]);
  const { rows, failed, loading, retry } = useAnswerRows(sessionId, refreshKey);
  const [filters, setFilters] = useState<CampaignExportFilters>(DEFAULT_FILTERS);
  // The last valid filters: an incomplete condition never empties the view.
  const [active, setActive] = useState<CampaignExportFilters>(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);

  /** Edit the filters, applying them as soon as they validate. */
  const updateFilters = useCallback((change: Partial<CampaignExportFilters>) => {
    const next = { ...filters, ...change };
    setFilters(next);
    try { setActive(validateExportFilters(next, fields)); } catch { /* Keep the last valid filters while a condition is incomplete. */ }
  }, [filters, fields]);

  const model = useMemo(() => rows && buildOverview(fields, rows, active, {
    onError: (error, question) => captureException(error instanceof Error ? error : new Error('Summary failed'), 'CampaignOverview.summarize', { interviewId: sessionId, fieldType: question.type }),
  }), [rows, fields, active, sessionId]);

  const telemetry = useCallback(() => model && {
    question_count: model.questions.length,
    response_count: model.population.length,
    has_filters: hasFilters(active),
  }, [model, active]);

  const viewed = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!model || viewed.current === sessionId) return;
    viewed.current = sessionId;
    captureEvent('campaign_overview_viewed', { field_types: [...new Set(model.questions.map(question => question.type))].sort(), ...telemetry() });
  }, [model, sessionId, telemetry]);

  /** Open the response list filtered to the respondents behind a mark. */
  const drill = useCallback((target: DrillTarget, label: string) => {
    if (!model || !onOpenResponses) return;
    const { ids } = drillDown(model, fields, target);
    // An empty mark has no respondents to list.
    if (ids.length) onOpenResponses({ ids, label });
  }, [model, fields, onOpenResponses]);

  /** Send a suggestion to Ask hollie and report where it came from. */
  const askSuggestion = useCallback((question: string) => {
    if (!onAsk) return;
    captureEvent('campaign_overview_ask_clicked', { source: 'suggestion', field_types: [], ...telemetry() });
    onAsk(question);
  }, [onAsk, telemetry]);

  /** Ask hollie about one card. */
  const askCard = useCallback((prompt: string, summary: QuestionSummary) => {
    if (!onAsk) return;
    captureEvent('campaign_overview_ask_clicked', { source: 'card', field_types: [summary.fieldType], ...telemetry() });
    onAsk(prompt);
  }, [onAsk, telemetry]);

  const questionsById = useMemo(() => new Map((model?.questions ?? []).map(question => [question.id, question])), [model]);

  /** The written summary beside the Ask hollie card (suggestions once answers load). */
  const summaryRow = (suggestions: string[] | undefined) => (summary || onAsk) && (
    <div className={`grid grid-cols-1 gap-4 ${summary && onAsk ? 'md:grid-cols-2' : ''}`}>
      {summary}
      {onAsk && <AskHollieCard key={sessionId} suggestions={suggestions} onAsk={askSuggestion} action={askBox} />}
    </div>
  );

  const cardHandlers = { questions: questionsById, onDrill: onOpenResponses ? drill : undefined, onAsk: onAsk ? askCard : undefined };
  const inputClass = 'w-full min-w-0 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground [color-scheme:light] dark:[color-scheme:dark] focus-visible:outline-2 focus-visible:outline-ring';

  return (
    <TipLayer>
      <section aria-label="Overview" aria-busy={loading} className="flex flex-col gap-5">
        {loading && <span role="status" aria-label="Loading analytics" aria-busy="true" className="sr-only" />}
        <fieldset disabled={!session} className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <FilterChipRow
              ariaLabel="Filter analytics"
              options={STATUS_OPTIONS.map(status => ({ value: status, label: responseStatusConfig[status].label }))}
              selected={filters.status ?? null}
              onSelect={status => updateFilters({ status: status === null ? undefined : status as ResponseStatus })}
            />
            <button type="button" aria-label="Filters" aria-expanded={showFilters} onClick={() => setShowFilters(value => !value)}
              className={`flex h-9 w-9 items-center justify-center rounded-full hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-ring ${hasFilters({ ...filters, status: DEFAULT_FILTERS.status }) ? 'text-accent-soft' : 'text-muted-foreground'}`}>
              <IconFilter aria-hidden="true" className="h-4 w-4" />
            </button>
            {updated && <div className="ml-auto">{updated}</div>}
          </div>
          {showFilters && (
            <div className="flex flex-col gap-3 rounded-[20px] border border-border/50 bg-surface p-5 light:border-transparent">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input type="date" aria-label="From date" value={filters.startDate ?? ''} className={inputClass}
                  onChange={event => updateFilters({ startDate: event.target.value || undefined, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone })} />
                <input type="date" aria-label="To date" value={filters.endDate ?? ''} min={filters.startDate} className={inputClass}
                  onChange={event => updateFilters({ endDate: event.target.value || undefined, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone })} />
              </div>
              <CampaignAnswerFilters fields={fields} filters={filters} inputClass={inputClass} onChange={updateFilters} />
            </div>
          )}
        </fieldset>

        {failed && <div role="alert" className="flex items-center justify-between rounded-[20px] border border-border/50 bg-surface px-6 py-4 light:border-transparent">
          <span className="text-sm text-danger-soft">Could not load answers.</span>
          <button type="button" onClick={retry} className="rounded-full px-4 py-1.5 text-sm text-foreground hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-ring">Retry</button>
        </div>}
        {(!failed || model) && <HeadlineStrip key={`headline:${sessionId}`} headline={model?.headline} />}
        {summaryRow(model ? buildSuggestions(model) : failed ? [] : undefined)}
        {(!failed || model) && <HighlightsCarousel key={`highlights:${sessionId}`} items={model?.highlights} {...cardHandlers} />}
        {recipeResults}
        {report}
        {model && <SectionRows sections={model.sections} {...cardHandlers} />}
        {model && <DropOffRow dropOff={model.dropOff} />}
      </section>
    </TipLayer>
  );
};

export default CampaignOverview;
